import { useEffect, useRef, useState } from 'react';
import { api, uploadToSignedUrl } from '../../core/api/client';
import { SelfieCamera } from './SelfieCamera';
import { reportError } from '../../core/telemetry/report';
import { t } from '../../platform/lang';
import { saveScanForLater } from '../../core/offline/idscans';
import { CONTACTS, dial } from '../help/HelpScreen';

/**
 * Face scan mid-route: whoever is holding the phone is scanned and measured
 * against the baseline the office approved for this driver.
 *
 * Called a scan rather than a photo, consistently with the enrollment screen and
 * for the same reason — the deterrent depends on drivers understanding their
 * face is being checked, not merely photographed. It is an accurate name: the
 * image is compared by face recognition, not filed away.
 *
 * Why this exists: device binding stopped drivers sharing a login, but a phone
 * handed over with its passcode still passes every sign-in — WebAuthn accepts
 * the passcode and will not say whether a face or a PIN was used. A camera of
 * our own, at a moment the driver cannot predict, is the only thing a web app
 * can do about that.
 *
 * THERE IS NO SKIP. There used to be — a link reading "Skip for now and carry
 * on" that called onFinished() and sent NOTHING, so a driver could decline every
 * scan of every route and leave no trace at all. That was a hole straight
 * through the middle of the one thing this app exists to do, and it should
 * never have been written.
 *
 * What replaces it, and the shape the owner asked for:
 *
 *   1st failure  — say what went wrong and what to change (light, hat, mask,
 *                  sunglasses) and scan again. Most failures are a dark car
 *                  park at 8pm, not a substitution.
 *   2nd failure  — tell them plainly that the office has been notified, and let
 *                  them carry on with the delivery.
 *
 * Both attempts are POSTed and recorded, so if it really was somebody else, the
 * record shows two failed scans rather than a shrug.
 *
 * It still NEVER BLOCKS. A false negative that stranded medicine would be far
 * worse than a substitution caught an hour later by a phone call. The
 * difference is that carrying on is now something the office is told about,
 * rather than something the driver chooses in silence.
 *
 * A comparison we could not perform — the service is down, or the office never
 * approved a baseline — is nobody's fault and is waved through without a word.
 * Making a driver re-scan for an office omission would be punishing them for
 * our problem.
 */

type Phase = 'intro' | 'camera' | 'working' | 'retry' | 'notified' | 'saved' | 'waiting';

/**
 * How many times we ask for a face before giving up and telling the office.
 *
 * ONLY APPLIES TO 'no_face'. Producing a picture with a face in it is entirely
 * within the driver's hands — point the camera at yourself — so it is fair to
 * ask more than once. Three tries covers a dark loading bay and a bad angle
 * without becoming a wall someone cannot get past.
 *
 * It still does not hard-lock at the end. A driver in an underground car park
 * at night must not be stranded over a photograph, so the last attempt lets
 * them carry on and puts it on the record instead. See HANDOFF §29h.
 */
const NO_FACE_ATTEMPTS = 3;

/**
 * Retries offered for a mismatch, and for a check we could not complete.
 *
 * A mismatch may be a hat, low sun or a smeared lens, so a second go is fair.
 * It is not made compulsory: carrying on is always one tap away, because a
 * face that genuinely will not match must never strand a delivery.
 */
const OTHER_ATTEMPTS = 2;

/**
 * The ceiling on scans for one check, however the driver got here.
 *
 * Once the attempts are spent, Continue is the main button — but a quiet
 * "scan again" stays available, because somebody who has just taken their hat
 * off should be able to put it right rather than carry a failed scan for the
 * rest of the day. Capped so it cannot become unlimited attempts at slipping
 * something past the comparison. Every attempt is stored either way.
 */
const MAX_TOTAL_ATTEMPTS = 4;

/**
 * NOTHING ADVANCES BY ITSELF EXCEPT A MATCH.
 *
 * Every other outcome now stops on a screen the driver has to read and dismiss.
 * The owner scanned a face on a television, saw something flash, and was on the
 * tote count before he could read it — which teaches a driver that the check can
 * quietly come to nothing, and teaches the office nothing at all.
 *
 * Even an `error`, which is OUR failure and never the driver's, now stops and
 * says so. It still does not accuse: the record says `error`, nothing claims
 * they did anything wrong, and Continue is always available. What changes is
 * that it is no longer silent.
 */

export function IdentityCheck({ trigger, taskId, live, onRecorded, onFinished }: {
  trigger: 'route_start' | 'random_stop' | 'manual';
  taskId?: string | null;
  /**
   * Called ONLY when the office has actually recorded a scan.
   *
   * This is what clears an owed check. It is deliberately separate from
   * onFinished, which fires whenever the driver leaves this screen for any
   * reason at all — including one where nothing reached us.
   */
  onRecorded?: () => void;
  onFinished: () => void;
  /** 134: the last scan on this route did not pass, so this one needs signal. */
  live?: boolean;
}) {
  const [phase, setPhase] = useState<Phase>('intro');
  const [attempt, setAttempt] = useState(0);
  const [lastResult, setLastResult] = useState<string | null>(null);
  // The short reference shown on a failed check, for reading to the office.
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Why the stop is waiting for the office (17 Oct): the scan could not be taken.
  const [waitReason, setWaitReason] = useState<'camera_failed' | 'no_face' | 'scan_not_saved'>('camera_failed');

  const send = async (blob: Blob) => {
    setPhase('working');
    const n = attempt + 1;
    setAttempt(n);
    try {
      const signed = await api.signIdentity(blob.type || 'image/jpeg');
      await uploadToSignedUrl(signed.uploadUrl, blob, signed.requiredHeaders);
      const verdict = await api.submitIdentity({
        objectPath: signed.objectPath, trigger, taskId: taskId ?? null,
      });

      /*
       * Recorded. Whatever the verdict, a row now exists at the office — so
       * any debt from an earlier stop is genuinely paid and can be cleared.
       */
      onRecorded?.();

      // A match is the one thing that moves on by itself.
      if (!verdict.flagged) { onFinished(); return; }

      setLastResult(verdict.result);

      /*
       * ONE RULE, PICKUP AND DELIVERY ALIKE — the owner's decision on 22 Sep,
       * after a version that treated the two differently and made the most
       * important scan of the day the softest one.
       *
       *   match     — the only outcome that advances on its own.
       *   no_face   — ask again until a face is produced, three times, then
       *               carry on with the office told. No Continue before that:
       *               there is no picture of anybody yet to carry on from,
       *               and pointing the camera at your own face is entirely
       *               within the driver's hands.
       *   mismatch  — ask again, but Continue is on screen from the first
       *               failure, large and unmissable.
       *   error     — we could not check. Say so, offer another go, and let
       *               them continue. Never silent.
       */
      if (verdict.result !== 'match') setCode(verdict.code ?? null);

      const allowed = verdict.result === 'no_face' ? NO_FACE_ATTEMPTS : OTHER_ATTEMPTS;
      setPhase(n >= allowed ? 'notified' : 'retry');
      return;
    } catch {
      /*
       * NO SIGNAL (17 Oct): the scan is kept on the phone and sent when signal
       * returns, like a delivery photo. Owner: a face scan cannot be skipped in
       * any way -- so "no signal" no longer means "no scan".
       */
      // Not after a scan that did not pass (134): that one must be live.
      if (!live) {
        try {
          await saveScanForLater(blob, taskId ?? null, trigger);
          setPhase('saved');
          return;
        } catch { /* the phone could not even keep it: fall through */ }
      }
      /*
       * NOTHING REACHED THE OFFICE — no signal, or the upload failed.
       *
       * This used to call onFinished() in silence, which was the hole: the
       * driver carried on, the owed check had already been wiped, and a
       * ten-stop route ended with one scan. Now the debt SURVIVES (onRecorded
       * is deliberately not called) and the driver is told it will be asked
       * again, rather than quietly let off.
       *
       * Since 30 Oct (owner) it no longer carries on: with no scan sent and
       * none saved, the stop waits for the office to unlock it.
       */
      setLastResult('unreachable');
      setCode(null);
      setPhase(n >= OTHER_ATTEMPTS ? 'notified' : 'retry');
    }
  };

  if (phase === 'working') {
    return <main className="screen"><h1>{t('idChecking')}</h1>
      <p className="hint">{t('idOneMoment')}</p></main>;
  }

  /*
   * A CHECK THAT DID NOT PASS. One screen for all three reasons, because the
   * driver's next action is the same in each case: read it, then choose.
   *
   * Nothing here happens on a timer and nothing advances by itself. The owner
   * watched a result flash past him onto the next step, and a check the driver
   * never saw is a check that taught nobody anything.
   */
  if (phase === 'retry') {
    const couldNotCheck = lastResult === 'error' || lastResult === 'not_compared'
      || lastResult === 'unreachable';
    return (
      <main className="screen">
        <h1>{couldNotCheck ? t('idErrorTitle') : t('idTryAgainTitle')}</h1>

        <div className="banner warn-banner">
          {lastResult === 'unreachable' ? t(live ? 'idLiveBody' : 'idOfflineBody')
            : couldNotCheck ? t('idErrorBody')
            : lastResult === 'no_face' ? t('idNoFaceBody') : t('idMismatchBody')}
          {/*
            * A short reference, not the raw fault text. Enough to read down the
            * phone to the office; meaningless to anyone photographing the
            * screen.
            */}
          {couldNotCheck && code && (
            <><br /><small>{t('idErrorCode')}: {code}</small></>
          )}
        </div>

        {/* The tips are about taking a better photograph, so they belong only
            where a better photograph would help. */}
        {!couldNotCheck && (
          <ul className="hint">
            <li>{t('idTipLight')}</li>
            <li>{t('idTipCover')}</li>
            <li>{t('idTipStraight')}</li>
          </ul>
        )}

        <button className="btn primary big" onClick={() => setPhase('camera')}>
          {t('idScanAgain')}
        </button>

        {/*
          * CONTINUE, FROM THE FIRST FAILURE — but not for no_face.
          *
          * A mismatch may be a hat or the sun, and a check we could not perform
          * is our fault entirely; neither should trap somebody holding a tote.
          * So the way out is large and on screen immediately, and it says what
          * it costs: the office is told.
          *
          * no_face is the exception. There is no picture of a person yet, so
          * there is nothing to carry on from — and producing one is entirely
          * within the driver's hands. They get three goes before this appears.
          */}
        {lastResult !== 'no_face' && (
          <button className="btn secondary big" onClick={() => setPhase('notified')}>
            {lastResult === 'unreachable' ? t('idAskOffice') : t('idContinueFlagged')}
          </button>
        )}
      </main>
    );
  }

  /*
   * Out of attempts, or they chose to carry on. Calm and not accusatory — most
   * of these are still a dark photo, and an honest driver must not feel
   * accused. But it is said out loud that the office knows, which is the whole
   * deterrent, and it needs a deliberate tap to leave.
   */
  if (phase === 'notified') {
    const couldNotCheck = lastResult === 'error' || lastResult === 'not_compared'
      || lastResult === 'unreachable';
    return (
      <main className="screen">
        <h1>{couldNotCheck ? t('idErrorTitle') : t('idCarryOn')}</h1>
        <div className="banner warn-banner">
          {lastResult === 'unreachable' ? t(live ? 'idLiveBody' : 'idOfflineBody')
            : couldNotCheck ? t('idErrorBody') : t('idFlagged')}
          {couldNotCheck && code && (
            <><br /><small>{t('idErrorCode')}: {code}</small></>
          )}
        </div>
        {/* No face is the one result the driver controls, so it is never waved
            through (17 Oct): the office unlocks the stop after calling them. */}
        {(lastResult === 'no_face' || lastResult === 'unreachable') && taskId ? (
          <button className="btn primary big"
                  onClick={() => { setWaitReason(lastResult === 'no_face' ? 'no_face' : 'scan_not_saved'); setPhase('waiting'); }}>
            {t('idAskOffice')}
          </button>
        ) : (
          <button className="btn primary big" onClick={onFinished}>{t('continue')}</button>
        )}
        {(attempt < MAX_TOTAL_ATTEMPTS || lastResult === 'no_face') && (
          <button className="btn link" onClick={() => setPhase('camera')}>
            {t('idScanAgain')}
          </button>
        )}
      </main>
    );
  }

  if (phase === 'saved') {
    return (
      <main className="screen">
        <h1>{t('idSavedTitle')}</h1>
        <div className="banner">{t('idSavedBody')}</div>
        <button className="btn primary big" onClick={() => { onRecorded?.(); onFinished(); }}>
          {t('continue')}
        </button>
      </main>
    );
  }

  if (phase === 'waiting' && taskId) {
    return <WaitForOffice taskId={taskId} reason={waitReason}
                          onUnlocked={onFinished}
                          onTryAgain={() => { setError(null); setPhase('camera'); }} />;
  }

  if (phase === 'camera') {
    return (
      <main className="screen">
        <h1>{t('faceTitle')}</h1>
        <SelfieCamera
          hint={t('idCamHint')}
          shutterLabel={t('idShutter')}
          onCapture={(b) => void send(b)}
          onUnavailable={(m) => {
            /*
             * The camera could not open at all — a permission the driver cannot
             * grant from here, or hardware in use by something else. This is the
             * ONLY way past the check, it is not a button anybody can choose,
             * and it is recorded so it shows up as a fault rather than
             * vanishing. Being unable to photograph must not strand a delivery.
             */
            reportError('camera_failed', 'identity', m);
            setError(m);
            setPhase('intro');
          }}
        />
      </main>
    );
  }

  return (
    <main className="screen">
      <h1>{t('faceTitle')}</h1>
      <p className="hint">
        {trigger === 'route_start' ? t('idStart') : t('idRandom')}
      </p>
      <p className="muted small">{t('idSmall')}</p>
      {error && (
        <>
          <div className="banner warn-banner">{error}</div>
          {/* No phone-camera-app backup (31 Oct): it could hand over a saved
              picture. A camera that will not open waits for the office. */}
          {/* Shown only after the camera has actually failed to open. */}
          <button className="btn link" onClick={() => {
            if (!taskId) { onFinished(); return; }
            setWaitReason('camera_failed'); setPhase('waiting');
          }}>{t('idCameraStuck')}</button>
        </>
      )}
      <button className="btn primary big" onClick={() => { setError(null); setPhase('camera'); }}>
        {t('faceStart')}
      </button>
    </main>
  );
}

/**
 * THE STOP WAITS FOR THE OFFICE (17 Oct). Owner: "face scan cannot be skipped
 * in any way." When the camera will not open, or no face can be produced, the
 * office is told at once and unlocks the stop on the dashboard after calling
 * the driver. Asked again every 10 seconds; the driver can also try the camera
 * again at any time.
 */
function WaitForOffice({ taskId, reason, onUnlocked, onTryAgain }: {
  taskId: string;
  reason: 'camera_failed' | 'no_face' | 'scan_not_saved';
  onUnlocked: () => void;
  onTryAgain: () => void;
}) {
  const [reached, setReached] = useState<boolean | null>(null);
  // Held in a ref so a parent re-render does not restart the asking.
  const done = useRef(onUnlocked);
  done.current = onUnlocked;
  useEffect(() => {
    let stop = false;
    const ask = async () => {
      try {
        const r = await api.identityUnlockRequest(taskId, reason);
        if (stop) return;
        setReached(true);
        if (r.unlocked) { stop = true; done.current(); }
      } catch {
        if (!stop) setReached(false);
      }
    };
    void ask();
    const timer = setInterval(() => { if (!stop) void ask(); }, 10_000);
    return () => { stop = true; clearInterval(timer); };
  }, [taskId, reason]);

  const office = CONTACTS[0];
  return (
    <main className="screen">
      <h1>{t('idWaitTitle')}</h1>
      <div className="banner warn-banner">
        {reached === false ? t('idWaitNoSignal') : t('idWaitBody')}
      </div>
      {office && office.numbers[0] && (
        <a className="btn primary big" href={`tel:${dial(office.numbers[0])}`}>
          {office.name} · {office.numbers[0]}
        </a>
      )}
      <button className="btn secondary big" onClick={onTryAgain}>{t('idWaitTryCamera')}</button>
    </main>
  );
}
